import React from "react";
import Select from "react-select";
import { FaExclamationTriangle, FaTrash } from "react-icons/fa";
import { formatSalesValue } from "../../../utils/formatUtils";
import { FaSpinner } from "react-icons/fa6";

const ConversionFactorInput = ({
    rows,
    addRow,
    removeRow,
    handleRowChange,
    loading,
    productionRecipeOptions,
    finishedGoodsOptions,
    getMenuOptionsForRow,
    menuLoading,
    menuError,
    selectStyles,
    editableClass,
}) => {
    return (
        <div className="space-y-4 max-h-[500px] overflow-y-auto tiny-scrollbar">
            {rows.map((row, index) => (
                <div
                    key={index}
                    className="grid grid-cols-1 md:grid-cols-6 gap-x-3 gap-y-2 items-start border border-gray-100 rounded-lg p-3 bg-gray-50"
                >
                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Production Item
                        </label>

                        <Select
                            options={finishedGoodsOptions}
                            value={
                                row.finishedGoods
                                    ? {
                                        label: `${row.finishedGoods.ItemName} (${row.finishedGoods.UOM})`,
                                        value: row.finishedGoods.ItemName,
                                    }
                                    : null
                            }
                            onChange={(selected) =>
                                handleRowChange(
                                    index,
                                    "finishedGoods",
                                    selected?.item || null
                                )
                            }
                            placeholder="Select finished goods"
                            styles={selectStyles}
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />

                        <div className="mt-1.5 text-sm text-gray-600">
                            Unit Cost:{" "}
                            {row.productionRecipe && row.factor > 0
                                ? `${formatSalesValue(
                                    Number(row.productionRecipe.sellingprice || 0) / Number(row.factor)
                                )}`
                                : "-"}
                        </div>
                    </div>

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Conversion Factor
                        </label>

                        <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={
                                row.factor
                            }
                            onChange={(
                                e
                            ) =>
                                handleRowChange(
                                    index,
                                    "factor",
                                    e.target.value
                                )
                            }
                            className={
                                editableClass
                            }
                            placeholder="0.00"
                        />
                    </div>

                    <div className="md:col-span-1 flex justify-end">
                        <button
                            type="button"
                            onClick={() =>
                                removeRow(index)
                            }
                            disabled={
                                rows.length === 1
                            }
                            className="w-9 h-9 flex items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40"
                        >
                            <FaTrash size={12} />
                        </button>

                    </div>

                    <div className="md:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">
                            Production Recipe
                        </label>

                        <Select
                            options={productionRecipeOptions}
                            value={
                                row.productionRecipe
                                    ? {
                                        label: row.productionRecipe.name,
                                        value: row.productionRecipe.id,
                                    }
                                    : null
                            }
                            onChange={(selected) =>
                                handleRowChange(
                                    index,
                                    "productionRecipe",
                                    selected?.recipe || null
                                )
                            }
                            placeholder="Select production recipe"
                            styles={selectStyles}
                            isSearchable
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />

                        <div className="mt-1.5 text-sm text-gray-600">
                            Cost:{" "}
                            {row.productionRecipe ? (
                                `${formatSalesValue(row.productionRecipe.sellingprice)}`
                            ) : ("-")}
                        </div>
                    </div>

                    <div className="md:col-span-3">
                        <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                            Link to Menu
                        </label>

                        {!row.finishedGoods ? (
                            <div className="mt-1.5 flex items-center justify-center gap-1.5 text-sm font-semibold italic text-gray-600 p-1.5 bg-white border border-gray-500 rounded-md shadow-sm">
                                Please select a production item
                            </div>
                        ) : (
                            <>
                                {menuLoading && (
                                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                                        <FaSpinner className="animate-spin text-gray-400" size={12} />
                                        Loading menu list
                                    </div>
                                )}

                                {menuError && (
                                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                                        <FaExclamationTriangle size={11} />
                                        <span>{menuError}</span>
                                    </div>
                                )}

                                <Select
                                    options={getMenuOptionsForRow(row)}
                                    isLoading={menuLoading}
                                    isDisabled={!!menuError}
                                    value={
                                        row.linkedMenuItem
                                            ? {
                                                label: `${row.linkedMenuItem.Name} (${row.linkedMenuItem.Description})`,
                                                value: row.linkedMenuItem.Name,
                                            }
                                            : null
                                    }
                                    onChange={(selected) =>
                                        handleRowChange(index, "linkedMenuItem", selected?.item || null)
                                    }
                                    placeholder="Select menu item"
                                    styles={selectStyles}
                                    isSearchable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />

                                {row.linkedMenuItem && (
                                    <div className="mt-1.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-md p-2">
                                        <div className="flex justify-between">
                                            <span className="font-medium text-gray-800">{row.linkedMenuItem.Name}</span>
                                            <span>{formatSalesValue(row.linkedMenuItem.Price)}</span>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {row.linkedMenuItem.Description} · {row.linkedMenuItem.Type}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            ))}

        </div>
    )
}

export default ConversionFactorInput